class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    facetForm.addEventListener('change', this.debouncedOnSubmit.bind(this));
    // The filter drawer's "Apply" button is type=submit. Without a submit
    // listener the form does a native navigation (full page reload) instead of
    // AJAX-filtering; onSubmitHandler preventDefaults and renders the filtered
    // section in place. Also covers Enter in a price/text input.
    facetForm.addEventListener('submit', this.onSubmitHandler.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static isRemovingFilter = false;

  static renderPage(searchParams, event, updateURLHash = true, isFacetRemoveOverride = false) {
    /* Preserve active price filter when the submitting form doesn't
       have price inputs (pills form, sort). If the form DID have price
       inputs, trust its values — the user may have cleared the filter.
       Also trust any `facet-remove` chip click: the chip's href is the
       canonical "everything except this one filter" URL, so if the user
       clicks the price chip the new params intentionally lack price and
       we must NOT restore it.

       `isFacetRemoveOverride` is used by call sites that have already
       removed the `<facet-remove>` node from the DOM before calling us,
       so `event.target.closest('facet-remove')` would return null. */
    const newParams = new URLSearchParams(searchParams);
    const formWithPrice = event?.target?.closest('form')?.querySelector('[data-price-gte]');
    const isFacetRemove = isFacetRemoveOverride || !!event?.target?.closest('facet-remove');
    if (!formWithPrice && !isFacetRemove && !newParams.has('filter.v.price.gte')) {
      const currentUrl = new URLSearchParams(window.location.search);
      const currentGte = currentUrl.get('filter.v.price.gte');
      const currentLte = currentUrl.get('filter.v.price.lte');
      if (currentGte) {
        newParams.set('filter.v.price.gte', currentGte);
        if (currentLte) newParams.set('filter.v.price.lte', currentLte);
      }
    }
    searchParams = newParams.toString();

    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.add('loading');
    FacetFiltersForm.showShimmer(true);

    // Show loading overlay on filter drawer
    FacetFiltersForm.showFilterLoading(true);
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      // Always fetch fresh — cache causes stale filter states
      FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    const currentActive = document.querySelector('.view-toggle-btn.active')
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];

        try {
          const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
          FacetFiltersForm.renderProductGridContainer(html, currentActive);
          FacetFiltersForm.renderProductCount(html);

          // Replace filter wrappers with fresh HTML (handles add/remove of filter groups)
          // Preserve open accordion states and checkbox states
          ['FacetsWrapperDesktop', 'FacetsWrapperMobile'].forEach((id) => {
            const newEl = parsedHTML.getElementById(id);
            const curEl = document.getElementById(id);
            if (newEl && curEl) {
              // Save open accordion IDs
              const openAccordions = new Set();
              curEl.querySelectorAll('details.js-filter[open]').forEach((d) => {
                if (d.id) openAccordions.add(d.id);
              });
              // Save the open state of the sort dropdown
              const sortDropdown = curEl.querySelector('.ek-sort-dropdown');
              const sortDropdownWasOpen = sortDropdown && sortDropdown.hasAttribute('open');
              // Save currently checked boxes (only when adding filters, not removing)
              const currentChecked = new Set();
              if (!FacetFiltersForm.isRemovingFilter) {
                curEl.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => {
                  currentChecked.add(cb.name + '=' + cb.value);
                });
              }
              curEl.innerHTML = newEl.innerHTML;
              // Restore open accordions
              openAccordions.forEach((accId) => {
                const acc = curEl.querySelector('#' + CSS.escape(accId));
                if (acc) acc.open = true;
              });
              // Re-check any boxes the user checked during AJAX flight
              currentChecked.forEach((key) => {
                const [name, value] = key.split('=');
                const cb = curEl.querySelector('input[type="checkbox"][name="' + name + '"][value="' + value + '"]');
                if (cb && !cb.checked) cb.checked = true;
              });
              // Keep focus inside the drawer to prevent MenuDrawer.onFocusOut from closing it
              var menuDrawer = curEl.closest('menu-drawer');
              if (menuDrawer && (menuDrawer.classList.contains('ek-drawer-open') || menuDrawer.querySelector('details[open]'))) {
                var focusTarget = curEl.querySelector('summary, button, input, a');
                if (focusTarget) focusTarget.focus({ preventScroll: true });
              }
            }
          });

          try { FacetFiltersForm.renderActiveFacets(parsedHTML); } catch(e) {}
          try { FacetFiltersForm.renderAdditionalElements(parsedHTML); } catch(e) {}
          FacetFiltersForm.renderFilterPills(html);
          // Re-inject search page pills if they were destroyed by grid replacement
          if (typeof window.__ekInjectSearchFilters === 'function') window.__ekInjectSearchFilters();
          if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
        } finally {
          FacetFiltersForm.showFilterLoading(false);
          FacetFiltersForm.showShimmer(false);
          // Remove any stale loading overlays
          document.querySelectorAll('.ek-filter-loading-overlay').forEach(function(el) { el.remove(); });
          // Reset removal flag
          FacetFiltersForm.isRemovingFilter = false;

          // Re-sort product cards by displayed price within each group
          FacetFiltersForm.resortByVisiblePrice();
          FacetFiltersForm.sinkOutOfStock();
          // Re-init sort dropdown listeners (old ones destroyed by innerHTML replacement)
          if (typeof ekInitSortDropdown === 'function') ekInitSortDropdown();
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const currentActive = document.querySelector('.view-toggle-btn.active')
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html, currentActive);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html, currentActive) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    // Try ProductGridContainer first, fallback to product-grid parent
    let gridContainer = document.getElementById('ProductGridContainer');
    let parsedGrid = parsedHTML.getElementById('ProductGridContainer');

    if (!gridContainer) {
      // Fallback: find the product-grid element's parent
      const liveGrid = document.getElementById('product-grid');
      const fetchedGrid = parsedHTML.getElementById('product-grid');
      if (liveGrid && fetchedGrid) {
        gridContainer = liveGrid.parentElement;
        parsedGrid = fetchedGrid.parentElement;
      }
    }

    if (!gridContainer || !parsedGrid) return;
    gridContainer.innerHTML = parsedGrid.innerHTML;

    /* Scroll the grid back into view after a filter/sort apply — the
       previous scroll position is rarely useful once the result set
       changes (the product the user was looking at may no longer be
       present, or the list may now be much shorter). Offset from the
       container top accounts for the sticky header. Only scroll UP
       when the user's actually scrolled past the grid; no jump if
       they're already above it. */
    const gridRect = gridContainer.getBoundingClientRect();
    const targetY = Math.max(0, window.scrollY + gridRect.top - 100);
    if (window.scrollY > targetY) {
      window.scrollTo({ top: targetY, behavior: 'smooth' });
    }

    gridContainer
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
      // const currentActive = document.querySelector('.view-toggle-btn.active')
      FacetFiltersForm.attachGridToggleListeners(currentActive);
      /* Re-sort variant tiles by their own price after AJAX replaces the grid.
         The server-rendered grid is sorted at product level, but variant
         flattening interleaves prices — this fixes the visual order. */
      FacetFiltersForm.resortByVisiblePrice();
      FacetFiltersForm.sinkOutOfStock();

      const gridItems = document.querySelectorAll('[data-type-li]')
      document.querySelectorAll('[data-search-type-trigger]').forEach(tab => {
        tab.addEventListener('click', function() {
          // Hide all tab contents
          gridItems.forEach(item => {
            item.style.display = 'none';
          });
          // Remove active class from all tabs
          document.querySelectorAll('[data-search-type-trigger]').forEach(t => t.classList.remove('active'));
          
          if(this.id == 'search-show-products') {
            gridItems.forEach(item => {
              if (item.dataset.typeLi == 'product') {
                item.style.display = 'block';
              }
            });
          } else if (this.id == 'search-show-posts') {
            gridItems.forEach(item => {
              if (item.dataset.typeLi == 'article') {
                item.style.display = 'block';
              }
            });
          }
          // Set the clicked tab to active
          this.classList.add('active');
        });
      });
    }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilterPills(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const pillsContainers = document.querySelectorAll('[id^="ek-filter-pills-"]');
    pillsContainers.forEach((container) => {
      const newPills = parsedHTML.getElementById(container.id);
      if (newPills) {
        container.innerHTML = newPills.innerHTML;
      }
    });
    // Also update pills that don't have IDs (e.g., search page pills injected by JS)
    FacetFiltersForm.updatePillActiveStates();
  }

  static updatePillActiveStates() {
    // Update active count on pills based on current URL params
    const params = new URLSearchParams(window.location.search);
    document.querySelectorAll('.ek-filter-pill:not(.ek-filter-pill--primary)').forEach(function(pill) {
      var filterParam = pill.dataset.filterParam;
      if (!filterParam) return;
      // Count active values for this filter param
      var count = 0;
      params.forEach(function(value, key) {
        if (key === filterParam) count++;
      });
      // Strip ALL existing counts like (1), (2) etc — handle multiple
      var label = pill.textContent.replace(/\s*\(\d+\)/g, '').trim();
      pill.textContent = count > 0 ? label + ' (' + count + ')' : label;
    });
  }

  static showShimmer(show) {
    // Find the collection div or product-grid parent
    var collection = document.querySelector('.collection') || document.getElementById('product-grid')?.parentElement;
    if (!collection) return;

    var shimmer = collection.querySelector('.ek-shimmer-grid');
    if (show) {
      /* Pin the collection container's height to its current rendered height
         BEFORE we hide the real grid. Otherwise the document shrinks (the
         shimmer is much shorter than the real grid), the browser clamps the
         user's scrollY to the new document height, and on the next render
         they end up scrolled to the top. Restored in showShimmer(false). */
      collection.dataset.ekHeightLock = collection.offsetHeight;
      collection.style.minHeight = collection.offsetHeight + 'px';

      // Hide real product grid
      var grid = collection.querySelector('#product-grid');
      if (grid) grid.style.display = 'none';

      if (!shimmer) {
        shimmer = document.createElement('div');
        shimmer.className = 'ek-shimmer-grid';
        shimmer.style.cssText = 'display:grid;grid-template-columns:repeat(2,1fr);gap:16px;padding:16px 0;';
        if (window.innerWidth >= 750) shimmer.style.gridTemplateColumns = 'repeat(4,1fr)';
        var cardHTML = '<div style="display:flex;flex-direction:column;gap:12px;">' +
          '<div style="width:100%;padding-bottom:100%;background:#f0efeb;position:relative;overflow:hidden;"><div style="position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.5),transparent);animation:ek-shimmer 1.5s infinite;"></div></div>' +
          '<div style="height:12px;width:60%;background:#f0efeb;border-radius:4px;position:relative;overflow:hidden;"><div style="position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.5),transparent);animation:ek-shimmer 1.5s infinite;"></div></div>' +
          '<div style="height:14px;width:85%;background:#f0efeb;border-radius:4px;position:relative;overflow:hidden;"><div style="position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.5),transparent);animation:ek-shimmer 1.5s infinite;"></div></div>' +
          '<div style="height:12px;width:50%;background:#f0efeb;border-radius:4px;position:relative;overflow:hidden;"><div style="position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.5),transparent);animation:ek-shimmer 1.5s infinite;"></div></div>' +
          '<div style="height:18px;width:40%;background:#f0efeb;border-radius:4px;position:relative;overflow:hidden;"><div style="position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.5),transparent);animation:ek-shimmer 1.5s infinite;"></div></div>' +
          '<div style="height:40px;width:100%;background:#f0efeb;position:relative;overflow:hidden;"><div style="position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.5),transparent);animation:ek-shimmer 1.5s infinite;"></div></div>' +
          '</div>';
        shimmer.innerHTML = cardHTML.repeat(8);
        collection.appendChild(shimmer);
      } else {
        shimmer.style.display = 'grid';
      }
    } else {
      // Show real product grid
      var grid = collection.querySelector('#product-grid');
      if (grid) grid.style.display = '';
      if (shimmer) shimmer.remove();
      /* Release the height lock — let the new grid drive container height. */
      collection.style.minHeight = '';
      delete collection.dataset.ekHeightLock;
    }
  }

  static showFilterLoading(show) {
    // Try drawer first, fallback to desktop filter wrapper
    let container = document.querySelector('menu-drawer.ek-drawer-open .mobile-facets__inner')
      || document.querySelector('#FacetsWrapperDesktop')
      || document.querySelector('#FacetsWrapperMobile');
    if (!container) return;
    let spinner = container.querySelector('.ek-filter-loading-overlay');
    if (show) {
      if (!spinner) {
        spinner = document.createElement('div');
        spinner.className = 'ek-filter-loading-overlay';
        spinner.innerHTML = '<div class="ek-filter-loading-spinner"></div>';
        container.style.position = 'relative';
        container.appendChild(spinner);
      }
      spinner.style.display = 'flex';
    } else {
      if (spinner) spinner.style.display = 'none';
    }
  }

  static resortByVisiblePrice() {
    var sortBy = new URLSearchParams(window.location.search).get('sort_by');
    if (!sortBy || (sortBy !== 'price-ascending' && sortBy !== 'price-descending')) return;
    var asc = sortBy === 'price-ascending';

    /* Handle two grid shapes:
       - .ek-product-grid > .ek-product-item   (used by type-product-grid, etc.)
       - .ek-variant-grid > li                 (used by product-variant-flat-grid;
                                                one tile per variant — sorting at
                                                variant-price level fixes the
                                                interleaved-price visual when
                                                Shopify's product-level sort
                                                clusters variants by product). */
    /* Sort only the items that actually carry a [data-sort-price]. Items
       without one (e.g. the "100% Non-Toxic / Superior Non-Stick / Designed
       in California" icons banner, review banner, video banner — injected
       inline as <li> children inside the product grid) stay anchored to
       their original positions. Without this, banners slot to position 0
       on ascending and last on descending sort. */
    var sortGrid = function(grid, itemSelector) {
      var items = Array.from(grid.querySelectorAll(':scope > ' + itemSelector));
      if (items.length < 2) return;
      var priceFor = function(el) {
        var node = el.querySelector('[data-sort-price]');
        return node ? parseInt(node.dataset.sortPrice || '0', 10) : null;
      };
      var sortable = [];
      var anchors = [];  // { index, node }  for non-sortable items in their original slots
      items.forEach(function(item, i) {
        var price = priceFor(item);
        if (price === null) {
          anchors.push({ index: i, node: item });
        } else {
          sortable.push({ price: price, node: item });
        }
      });
      if (sortable.length < 2) return;
      sortable.sort(function(a, b) { return asc ? a.price - b.price : b.price - a.price; });
      /* Rebuild the new order: walk slot indices 0..N-1, place the next
         sorted item except where an anchor occupies that slot. */
      var sortedQueue = sortable.map(function(s) { return s.node; });
      var newOrder = [];
      var anchorMap = {};
      anchors.forEach(function(a) { anchorMap[a.index] = a.node; });
      for (var i = 0; i < items.length; i++) {
        if (anchorMap[i]) {
          newOrder.push(anchorMap[i]);
        } else {
          newOrder.push(sortedQueue.shift());
        }
      }
      newOrder.forEach(function(node) { grid.appendChild(node); });
    };

    document.querySelectorAll('.ek-product-grid').forEach(function(grid) {
      sortGrid(grid, '.ek-product-item');
    });
    document.querySelectorAll('.ek-variant-grid').forEach(function(grid) {
      sortGrid(grid, 'li');
    });
  }

  /* Push sold-out tiles to the end of every collection grid, keeping the
     current order (server order, or the price order left by
     resortByVisiblePrice) within the in-stock and sold-out groups. Runs on
     load and after every AJAX facet/sort re-render, so it holds across all
     collections and all sort modes. Each tile's status comes from
     [data-oos] on the product card (true only for genuinely unavailable
     stock — continue-selling / pre-order tiles stay in place).
     Tiles without a product card (icon / review / video banners) are
     anchored to their original slots, matching resortByVisiblePrice. */
  static sinkOutOfStock() {
    var sinkGrid = function(grid, itemSelector) {
      var items = Array.from(grid.querySelectorAll(':scope > ' + itemSelector));
      if (items.length < 2) return;
      var oosOf = function(el) {
        var card = el.matches('[data-product-card]') ? el : el.querySelector('[data-product-card]');
        if (!card) return null;
        return card.getAttribute('data-oos') === 'true';
      };
      var inStock = [];
      var soldOut = [];
      var anchors = {};
      items.forEach(function(item, i) {
        var state = oosOf(item);
        if (state === null) anchors[i] = item;
        else if (state) soldOut.push(item);
        else inStock.push(item);
      });
      if (!soldOut.length) return;
      var queue = inStock.concat(soldOut);
      var newOrder = [];
      for (var i = 0; i < items.length; i++) {
        newOrder.push(anchors[i] || queue.shift());
      }
      newOrder.forEach(function(node) { grid.appendChild(node); });
    };

    document.querySelectorAll('.ek-product-grid').forEach(function(grid) {
      sinkGrid(grid, '.ek-product-item');
    });
    document.querySelectorAll('.ek-variant-grid').forEach(function(grid) {
      sinkGrid(grid, 'li');
    });
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        currentElement.innerHTML = elementToRender.innerHTML;
      } else {
        // Element doesn't exist in DOM — insert it in the correct position
        // Find the parent form wrapper
        const parentForm = elementToRender.closest('#FacetFiltersForm, #FacetFiltersFormMobile, #FacetFiltersPillsForm');
        const parentId = parentForm ? parentForm.id : null;
        const domParent = parentId ? document.getElementById(parentId) : null;

        if (domParent) {
          // Find the wrapper that holds filter details
          const wrapper = domParent.querySelector('#FacetsWrapperDesktop, #FacetsWrapperMobile') || domParent;
          const existingFilters = wrapper.querySelectorAll('.js-filter');

          if (existingFilters.length > 0) {
            // Insert after the last existing filter
            existingFilters[existingFilters.length - 1].after(elementToRender);
          } else {
            wrapper.appendChild(elementToRender);
          }
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    /* After AJAX re-render: restore price checkbox state from URL params
       and mark price-range as user-changed so subsequent filters preserve it. */
    (function() {
      var urlParams = new URLSearchParams(window.location.search);
      var urlGte = urlParams.get('filter.v.price.gte');
      var urlLte = urlParams.get('filter.v.price.lte');

      document.querySelectorAll('price-range').forEach(function(pr) {
        var gteInput = pr.querySelector('[data-price-gte]');
        var lteInput = pr.querySelector('[data-price-lte]');

        if (urlGte) {
          /* Set hidden inputs from URL */
          if (gteInput) gteInput.value = urlGte;
          if (lteInput) lteInput.value = urlLte || '';
          pr.dataset.userChanged = 'true';

          /* Check the matching checkbox */
          var checkboxes = pr.querySelectorAll('.ek-price-group__input');
          checkboxes.forEach(function(cb) {
            var minMatch = cb.dataset.priceMin === urlGte;
            var maxMatch = (!urlLte && cb.dataset.priceMax === '0') || cb.dataset.priceMax === urlLte;
            if (minMatch && maxMatch) {
              cb.checked = true;
              cb.closest('.ek-price-group').classList.add('ek-price-group--active');
            } else {
              cb.checked = false;
              cb.closest('.ek-price-group').classList.remove('ek-price-group--active');
            }
          });
        }
      });
    })();

    if (countsToRender && event?.target) {
      const closestJSFilterID = event.target.closest('.js-filter')?.id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement?.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement?.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        /* preventScroll: focus() on a desktop facet summary near the top of
           the page would otherwise scroll the viewport up after every sort/
           filter change — banner jumps to top. Match the preventScroll usage
           on line ~115. */
        if (newElementToActivate && !isTextInput) newElementToActivate.focus({ preventScroll: true });
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    /* There can be multiple `.active-facets-*` containers on the page (mobile
       + desktop + inline above grid). `querySelector` only updates the first,
       leaving the others stale — chips get out of sync with the URL after
       every filter change. Use `querySelectorAll` and update each live
       container with the fetched content at the matching index (fallback to
       the first fetched entry when the counts differ). */
    activeFacetElementSelectors.forEach((selector) => {
      const fetchedEls = html.querySelectorAll(selector);
      const liveEls = document.querySelectorAll(selector);
      if (fetchedEls.length === 0 || liveEls.length === 0) return;
      liveEls.forEach((live, idx) => {
        const fetched = fetchedEls[idx] || fetchedEls[0];
        live.innerHTML = fetched.innerHTML;
      });
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting', '[data-search-tabs]'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    // Re-bind mobile sort popup listeners on the live DOM
    updateSortPopup()
    // Only rebind events if drawer is closed — bindEvents re-attaches summary click which auto-closes open drawer
    var menuDrawer = document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer');
    if (menuDrawer && !menuDrawer.classList.contains('ek-drawer-open')) {
      menuDrawer.bindEvents();
    }

    // FacetFiltersForm.attachGridToggleListeners();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static attachGridToggleListeners(currentActive) {
    const gridTogglebtns = document.querySelectorAll('.view-toggle-btn')
    gridTogglebtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        gridTogglebtns.forEach(btn => { btn.classList.remove('active') })
        btn.classList.add('active')
        var pGrid = document.querySelector("#product-grid")
        if (window.location.href.includes('/search')) {
          pGrid = document.querySelector("#search-product-grid")
        }
        if (btn.id == 'grid-view-btn-single' && pGrid) {
          pGrid.classList.remove("grid-cols-2", "xl:grid-cols-3")
          pGrid.classList.add("grid-cols-1", "xl:grid-cols-2")
        } else if (pGrid) {
          pGrid.classList.add("grid-cols-2", "xl:grid-cols-3")
          pGrid.classList.remove("grid-cols-1", "xl:grid-cols-2")
        }
      })
    })
    if (currentActive) {
      // currentActive.click();
      // currentActive.classList.add('active')
      // document.querySelector('.view-toggle-btn:not(.active)').classList.remove('active')
    }
    // setTimeout(() => {
    // },10);
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const grid = document.getElementById('product-grid');
    let sectionId = grid?.dataset?.id;

    // Fallback: extract from shopify-section wrapper
    if (!sectionId) {
      const wrapper = document.querySelector('[id^="shopify-section-"][id*="product-grid"]');
      sectionId = wrapper?.id?.replace('shopify-section-', '');
    }

    // Fallback: extract from filter aside data-id
    if (!sectionId) {
      const aside = document.getElementById('main-collection-filters');
      sectionId = aside?.dataset?.id;
    }

    return [{ section: sectionId }];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams(formData);

    // Remove price params only if they're at default/empty values.
    // Previously checked userChanged flag, but that gets lost after AJAX.
    // Instead, check if the hidden inputs have meaningful values.
    // NOTE: the hidden inputs serialize floats as "0.0" (not "0"), so "0.0"
    // must be treated the same as "0"/empty — otherwise every non-price
    // filter submission ships `filter.v.price.gte=0.0` and Shopify renders
    // a bogus "₹0 - ₹0" chip.
    const isEmptyPrice = (v) => !v || v === '' || v === '0' || v === '0.0' || parseFloat(v) === 0;
    const priceGteInput = form.querySelector('[data-price-gte]');
    if (priceGteInput) {
      // Checkbox-based price filter — trust the hidden input values
      if (isEmptyPrice(priceGteInput.value)) {
        params.delete('filter.v.price.gte');
        params.delete('filter.v.price.lte');
      }
    } else {
      // Legacy range slider — keep old userChanged behavior
      const priceRange = form.querySelector('price-range');
      if (priceRange && !priceRange.dataset.userChanged) {
        params.delete('filter.v.price.gte');
        params.delete('filter.v.price.lte');
      }
    }

    // Clean up price params — remove if value is 0 or empty (no filter selected)
    if (isEmptyPrice(params.get('filter.v.price.gte'))) {
      params.delete('filter.v.price.gte');
    }
    if (isEmptyPrice(params.get('filter.v.price.lte'))) {
      params.delete('filter.v.price.lte');
    }

    // If this form doesn't have price inputs (e.g. pills form), preserve
    // active price params from the current URL so they don't get lost.
    if (!params.has('filter.v.price.gte') && !form.querySelector('[data-price-gte]')) {
      var urlParams = new URLSearchParams(window.location.search);
      var urlGte = urlParams.get('filter.v.price.gte');
      var urlLte = urlParams.get('filter.v.price.lte');
      if (urlGte) params.set('filter.v.price.gte', urlGte);
      if (urlLte) params.set('filter.v.price.lte', urlLte);
    }

    return params.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    // Clear cache to prevent stale filter HTML from being reused
    FacetFiltersForm.filterData = [];
    FacetFiltersForm.isRemovingFilter = true;
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url, event);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
// Re-sort on initial page load if sort_by is in URL
FacetFiltersForm.resortByVisiblePrice();
// Push sold-out tiles to the end on initial load (runs for every sort mode)
FacetFiltersForm.sinkOutOfStock();

// Filter pill click handler is in snippets/filter-pills.liquid (self-contained)

// class PriceRange extends HTMLElement {
//   constructor() {
//     super();
//     this.querySelectorAll('input').forEach((element) => {
//       element.addEventListener('change', this.onRangeChange.bind(this));
//       element.addEventListener('keydown', this.onKeyDown.bind(this));
//     });
//     this.setMinAndMaxValues();
//   }

//   onRangeChange(event) {
//     this.adjustToValidValues(event.currentTarget);
//     this.setMinAndMaxValues();
//   }

//   onKeyDown(event) {
//     if (event.metaKey) return;

//     const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
//     if (!event.key.match(pattern)) event.preventDefault();
//   }

//   setMinAndMaxValues() {
//     const inputs = this.querySelectorAll('input');
//     const minInput = inputs[0];
//     const maxInput = inputs[1];
//     if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
//     if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
//     if (minInput.value === '') maxInput.setAttribute('data-min', 0);
//     if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
//   }

//   adjustToValidValues(input) {
//     const value = Number(input.value);
//     const min = Number(input.getAttribute('data-min'));
//     const max = Number(input.getAttribute('data-max'));

//     if (value < min) input.value = min;
//     if (value > max) input.value = max;
//   }
// }

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.currencySymbol = this.dataset.currencySymbol; // Get the currency symbol from the data attribute

    this.rangeInputs = this.querySelectorAll('input.range');
    this.bubbles = this.querySelectorAll('.bubble');

    // Initialize bubbles and set min/max values based on existing filters
    this.rangeInputs.forEach((input, index) => {
      this.setBubble(input, this.bubbles[index]);
      if (index === 0) { // First range input
        this.rangeInputs[1].min = parseInt(input.max) + 1;
      }
    });

    // Add event listeners
    this.rangeInputs.forEach((input, index) => {
      input.addEventListener('input', () => this.onRangeInput(input, this.bubbles[index]));
    });
  }

  onRangeInput(range, bubble) {
    this.adjustToValidValues(range);
    this.setBubble(range, bubble);
    this.updateRangeMinMax();
  }

  setBubble(range, bubble) {
    const val = Math.round(range.value);  // Round the value to remove decimals
    bubble.innerHTML = `${this.currencySymbol}${val}`;
  }

  updateRangeMinMax() {
    const minInput = this.rangeInputs[0];
    const maxInput = this.rangeInputs[1];

    maxInput.min = parseInt(minInput.max) + 1;
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);


class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

function updateSortPopup() {
  var popupContent = document.getElementById('mobile-sort-popup-content');
  if (!popupContent) return;
  var currentSort = new URLSearchParams(window.location.search).get('sort_by') || '';
  var radios = popupContent.querySelectorAll('[type="radio"]');
  radios.forEach(function(radio) {
    // Remove old listeners by cloning
    var clone = radio.cloneNode(true);
    radio.parentNode.replaceChild(clone, radio);
    // Sync checked state with current URL
    if (currentSort) {
      clone.checked = (clone.value === currentSort);
      var label = clone.closest('.facets__label');
      if (label) label.classList.toggle('active', clone.checked);
    }
    clone.addEventListener('change', function() {
      var selectedValue = clone.checked ? clone.value : '';
      if (!selectedValue) return;
      // Build search params: current filters + new sort_by
      var params = new URLSearchParams(window.location.search);
      params.set('sort_by', selectedValue);
      FacetFiltersForm.renderPage(params.toString());
      // Close the sort popup
      var sortDetails = clone.closest('details');
      if (sortDetails) sortDetails.removeAttribute('open');
    });
  });
}

updateSortPopup()
